import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Banknote,
  Building2,
  CalendarPlus,
  CheckCircle2,
  ChevronRight,
  Loader2,
  PercentCircle,
  Search,
  ShieldCheck,
  Split,
  User as UsuarioIcono,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { AvisoDeConvenio } from '@/modules/convenios/AvisoDeConvenio';
import type { Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type {
  Cliente,
  DeudaParaCobrar,
  FacturaParaCobrar,
  PagadoHasta,
  PagoRegistrado,
  PredioParaCobrar,
  ProximoComprobante,
} from '@/shared/api/tipos';
import { Cargando } from '@/shared/ui/Cargando';
import { Modal } from '@/shared/ui/Modal';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { notificar } from '@/shared/ui/avisos';
import { nombreDelMes } from '@/shared/ui/SelectorDeMes';
import { GrupoDelArt8 } from './GrupoDelArt8';
import { PanelDeAnticipacion } from './PanelDeAnticipacion';
import { agruparPorConcepto, agruparPorCuenta, etiquetaDelConcepto } from './estados';

const MEDIOS = [
  { codigo: 'efectivo', nombre: 'Efectivo' },
  { codigo: 'transferencia', nombre: 'Transferencia' },
  { codigo: 'tarjeta', nombre: 'Tarjeta' },
  { codigo: 'deposito', nombre: 'Depósito' },
  { codigo: 'cheque', nombre: 'Cheque' },
] as const;

/**
 * Lo que se cobra con un segundo medio: «de los 39,38, veinte transferidos».
 *
 * Solo se escribe el importe del PRIMER medio —el de los botones— porque es el único dato que
 * la cajera tiene en la cabeza; el segundo se lleva el resto, y así nunca puede descuadrar.
 */
export interface Reparto {
  importe: string;
  medio: string;
  referenciaBancaria: string;
  banco: string;
}

const PIDE_REFERENCIA = (medio: string) => medio !== 'efectivo';
const PIDE_BANCO = (medio: string) => medio === 'cheque';

/** En centavos, que es lo único que no deja polvo al restar. */
function centavos(valor: number) {
  return Math.round(valor * 100);
}

function nombreDelMedio(codigo: string) {
  return MEDIOS.find((m) => m.codigo === codigo)?.nombre ?? codigo;
}

/** Lo que se lleva el segundo medio: el total menos lo del primero. */
function restoDelReparto(total: number, reparto: Reparto) {
  return (centavos(total) - centavos(Number(reparto.importe || 0))) / 100;
}

/** Devuelve el motivo por el que el reparto no sirve, o null si está bien. */
function queLeFaltaAlReparto(total: number, metodo: string, reparto: Reparto): string | null {
  const primero = Number(reparto.importe);
  if (!reparto.importe.trim() || Number.isNaN(primero) || primero <= 0) {
    return `Escriba cuánto se paga con ${nombreDelMedio(metodo).toLowerCase()}.`;
  }
  if (centavos(primero) >= centavos(total)) {
    return 'Lo del primer medio tiene que ser menos que el total: si no, no hay reparto.';
  }
  if (!reparto.medio) {
    return 'Elija con qué se paga el resto.';
  }
  if (reparto.medio === metodo) {
    return 'Los dos medios son el mismo: entonces es un cobro de un solo medio.';
  }
  if (PIDE_REFERENCIA(reparto.medio) && reparto.referenciaBancaria.trim() === '') {
    return `Falta la referencia de la parte en ${nombreDelMedio(reparto.medio).toLowerCase()}.`;
  }
  if (PIDE_BANCO(reparto.medio) && reparto.banco.trim() === '') {
    return 'Falta el banco girador del cheque.';
  }
  return null;
}

export function ModalDeCobro({
  alCerrar,
  alCobrado,
}: {
  alCerrar: () => void;

  alCobrado: (pago: PagoRegistrado) => void;
}) {
  const clienteDeConsultas = useQueryClient();

  const [busqueda, setBusqueda] = useState('');
  const [cliente, setCliente] = useState<Cliente | null>(null);

  const [desmarcadas, setDesmarcadas] = useState<Set<number>>(new Set());
  /**
   * Los predios que la cajera quitó, por el identificador de su línea.
   *
   * Es lo que permite que el inquilino de un departamento pague el suyo y los otros del
   * propietario sigan debiéndose. Se guarda lo QUITADO y no lo marcado para que todo venga
   * marcado por omisión, que es como se cobra casi siempre.
   */
  const [prediosFuera, setPrediosFuera] = useState<Set<number>>(new Set());
  const [metodo, setMetodo] = useState<string>('efectivo');
  const [numeroComprobante, setNumeroComprobante] = useState('');

  const [comprobanteTocado, setComprobanteTocado] = useState(false);

  const { perfil, puede } = useSesion();
  /** Cobrar sin la mora: solo con su permiso, y siempre diciendo por qué (el CUR, por ejemplo). */
  const [sinMora, setSinMora] = useState(false);
  const [motivoSinMora, setMotivoSinMora] = useState('');
  const [referenciaBancaria, setReferenciaBancaria] = useState('');
  const [banco, setBanco] = useState('');
  const [reparto, setReparto] = useState<Reparto | null>(null);
  const [observaciones, setObservaciones] = useState('');

  const [adelantando, setAdelantando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientes = useQuery({
    queryKey: ['clientes', 'cobros', busqueda],
    queryFn: () =>
      api.get<Pagina<Cliente>>('/clientes', { query: { texto: busqueda, tamanio: 8 } }),
    enabled: busqueda.trim().length >= 3,
  });

  const proximo = useQuery({
    queryKey: ['proximo-comprobante'],
    queryFn: () => api.get<ProximoComprobante>('/cobros/proximo-comprobante'),
    enabled: cliente !== null,

    staleTime: 0,
    gcTime: 0,
  });

  const comprobanteMostrado = comprobanteTocado ? numeroComprobante : (proximo.data?.numero ?? '');

  const sinCaja = proximo.isSuccess && !proximo.data.caja;

  const deuda = useQuery({
    queryKey: ['cobro-deuda', cliente?.id],
    queryFn: () => api.get<DeudaParaCobrar>(`/cobros/clientes/${cliente?.id}`),
    enabled: cliente !== null,
  });

  const facturas = useMemo(() => deuda.data?.facturas ?? [], [deuda.data]);

  const cobrar = useMutation({
    mutationFn: () => {
      const elCobro = {
        facturas: seleccion.map((f) => ({ id: f.id, historica: f.historica })),
        ...(prediosElegidos.length > 0 ? { predios: prediosElegidos } : {}),
        metodo,
        medios: reparto
          ? [
              {
                medio: metodo,
                importe: Number(reparto.importe),
                referenciaBancaria: referenciaBancaria.trim() || undefined,
                banco: metodo === 'cheque' ? banco.trim() || undefined : undefined,
              },
              {
                medio: reparto.medio,
                importe: restoDelReparto(capital + interesACobrar, reparto),
                referenciaBancaria: reparto.referenciaBancaria.trim() || undefined,
                banco: reparto.medio === 'cheque' ? reparto.banco.trim() || undefined : undefined,
              },
            ]
          : undefined,

        numeroComprobante: comprobanteTocado ? numeroComprobante.trim() || undefined : undefined,
        referenciaBancaria: referenciaBancaria.trim() || undefined,
        banco: metodo === 'cheque' ? banco.trim() || undefined : undefined,
        observaciones: observaciones.trim() || undefined,
      };
      return sinMora
        ? api.post<PagoRegistrado>(`/cobros/clientes/${cliente?.id}/sin-mora`, {
            cobro: elCobro,
            motivo: motivoSinMora.trim(),
          })
        : api.post<PagoRegistrado>(`/cobros/clientes/${cliente?.id}`, elCobro);
    },
    onSuccess: (pago) => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['proximo-comprobante'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cobro-deuda'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cartera-resumen'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cartera-deudores'] });

      notificar.exito(
        `Cobrado $${pago.total.toFixed(2)}${
          pago.numeroComprobante ? ` · ${pago.numeroComprobante}` : ''
        }`,
      );
      alCobrado(pago);
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo registrar el cobro';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  function elegirCliente(c: Cliente) {
    setCliente(c);
    setDesmarcadas(new Set());
    setPrediosFuera(new Set());

    setNumeroComprobante('');
    setComprobanteTocado(false);
    setReferenciaBancaria('');
    setReparto(null);
    setObservaciones('');
    setSinMora(false);
    setMotivoSinMora('');
    setError(null);
    cobrar.reset();
  }

  function alternar(id: number) {
    const siguiente = new Set(desmarcadas);
    if (siguiente.has(id)) {
      siguiente.delete(id);
    } else {
      siguiente.add(id);
    }
    setDesmarcadas(siguiente);
  }

  function alternarPredio(detalleId: number) {
    const siguiente = new Set(prediosFuera);
    if (siguiente.has(detalleId)) {
      siguiente.delete(detalleId);
    } else {
      siguiente.add(detalleId);
    }
    setPrediosFuera(siguiente);
  }

  /** Si el grupo está entero lo quita; si no, lo marca entero. */
  function alternarGrupo(delGrupo: FacturaParaCobrar[]) {
    const siguiente = new Set(desmarcadas);
    const entero = delGrupo.every((f) => !desmarcadas.has(f.id));
    delGrupo.forEach((f) => (entero ? siguiente.add(f.id) : siguiente.delete(f.id)));
    setDesmarcadas(siguiente);
    if (!entero) {
      const fuera = new Set(prediosFuera);
      delGrupo.forEach((f) => (f.predios ?? []).forEach((p) => fuera.delete(p.detalleId)));
      setPrediosFuera(fuera);
    }
  }

  function marcarTodas() {
    setDesmarcadas(new Set());
    setPrediosFuera(new Set());
  }

  function desmarcarTodas() {
    setDesmarcadas(new Set(facturas.map((f) => f.id)));
  }

  /** De una factura, los predios que siguen marcados. Vacío en las históricas. */
  function prediosDe(f: FacturaParaCobrar): PredioParaCobrar[] {
    return (f.predios ?? []).filter((p) => !prediosFuera.has(p.detalleId));
  }

  // Quitar todos los predios de una factura es quitar la factura: el servidor rechaza una
  // factura de la que no se eligió ninguno, y con razón.
  const seleccion = facturas.filter(
    (f) => !desmarcadas.has(f.id) && ((f.predios ?? []).length === 0 || prediosDe(f).length > 0),
  );

  function loDe(f: FacturaParaCobrar, cual: 'capital' | 'interes'): number {
    return (f.predios ?? []).length === 0
      ? f[cual]
      : prediosDe(f).reduce((suma, p) => suma + p[cual], 0);
  }

  const capital = seleccion.reduce((suma, f) => suma + loDe(f, 'capital'), 0);
  const interes = seleccion.reduce((suma, f) => suma + loDe(f, 'interes'), 0);
  const puedePerdonar = puede('pagos.perdonar_mora') && interes > 0;

  const art8 = cliente && (
    <GrupoDelArt8
      identificacion={cliente.identificacion}
      puedeVer={puede('titulos.ver')}
      puedeCobrar={puede('titulos.cobrar')}
      alCobrado={(pago) => {
        void clienteDeConsultas.invalidateQueries({ queryKey: ['titulos-deudores'] });
        alCobrado(pago);
      }}
    />
  );
  const perdonando = sinMora && puedePerdonar;
  /** Lo que de verdad se cobra de mora: cero si se cobra sin ella. */
  const interesACobrar = perdonando ? 0 : interes;
  const faltaElMotivo = perdonando && motivoSinMora.trim().length < 5;

  /** Los predios que se cobran. Vacío cuando van todos: el servidor lo entiende como «todos». */
  const prediosElegidos =
    prediosFuera.size === 0 ? [] : seleccion.flatMap((f) => prediosDe(f).map((p) => p.detalleId));

  const d = deuda.data;

  const faltaDelBanco =
    (metodo !== 'efectivo' && referenciaBancaria.trim() === '') ||
    (metodo === 'cheque' && banco.trim() === '');
  const faltaDelReparto = reparto
    ? queLeFaltaAlReparto(capital + interesACobrar, metodo, reparto)
    : null;
  const listo =
    seleccion.length > 0 &&
    !cobrar.isPending &&
    !faltaDelBanco &&
    faltaDelReparto === null &&
    !faltaElMotivo;

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Registrar cobro"

      subtitulo={rotuloDeLaCaja(perfil?.nombre, proximo.data?.nombreDeLaCaja)}
      icono={Banknote}
      ancho="ventanilla"

      cerrarAlPulsarFuera={false}
    >
      <BuscadorDeCliente
        busqueda={busqueda}
        alBuscar={setBusqueda}
        clientes={clientes.data?.contenido ?? []}
        buscando={clientes.isLoading}
        elegido={cliente}
        alElegir={elegirCliente}
        alLimpiar={() => {
          setCliente(null);
          setBusqueda('');
          setDesmarcadas(new Set());
          setPrediosFuera(new Set());
          setNumeroComprobante('');
          setComprobanteTocado(false);
          setReferenciaBancaria('');
          setReparto(null);
          setObservaciones('');
          setSinMora(false);
          setMotivoSinMora('');
          setError(null);
        }}
      />

      {cliente === null ? (
        <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
          Busque a la persona que viene a pagar
        </p>
      ) : deuda.isPending ? (
        <Cargando
          titulo="Consultando lo que debe"
          detalle="Calculando el interés factura a factura"
          alto="h-64"
        />
      ) : d && d.facturas.length === 0 ? (
        <div className="rounded-2xl border border-marca-200 bg-marca-50 p-12 text-center">
          <p className="flex items-center justify-center gap-3 text-sm font-bold uppercase text-marca-800">
            <CheckCircle2 className="size-5" aria-hidden />
            {cliente.nombre} no debe nada
          </p>
          <PagadoHastaDe cuentas={d.pagadoHasta ?? []} />
          <button
            type="button"
            onClick={() => setAdelantando(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-marca-300 bg-white px-4 py-2.5 text-[11px] font-semibold uppercase text-marca-700 transition-colors hover:bg-marca-100"
          >
            <CalendarPlus className="size-3.5" aria-hidden />
            Adelantar los meses que vienen
          </button>
          <div className="mt-6 text-left">{art8}</div>
        </div>
      ) : (
        d && (
          <div className="grid gap-5 lg:grid-cols-[1fr_20rem]">
            <div className="space-y-4">
              <Avisos deuda={d} />

              <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-armazon-100 px-5 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-armazon-600">
                    {d.facturas.length} facturas sin pagar · primero las más viejas
                  </p>
                  <span className="flex gap-1">
                    <Atajo alPulsar={marcarTodas} activo={desmarcadas.size === 0}>
                      Todas
                    </Atajo>
                    <Atajo alPulsar={desmarcarTodas} activo={desmarcadas.size === facturas.length}>
                      Ninguna
                    </Atajo>
                  </span>
                </div>

                <div className="max-h-[28rem] overflow-y-auto">
                  {agruparPorConcepto(facturas).map((grupo) => {
                    const marcadas = seleccion.filter((f) => f.concepto === grupo.concepto);
                    return (
                      <GrupoDeFacturas
                        key={grupo.concepto}
                        nombre={grupo.nombre}
                        cuantas={grupo.facturas.length}
                        marcadas={marcadas.length}
                        importe={marcadas.reduce(
                          (suma, f) => suma + loDe(f, 'capital') + loDe(f, 'interes'),
                          0,
                        )}
                        alAlternar={() => alternarGrupo(grupo.facturas)}
                      >
                        {(() => {
                          const cuentas = agruparPorCuenta(grupo.facturas);
                          const fila = (f: FacturaParaCobrar, sinCuenta: boolean) => (
                            <FilaDeFactura
                              key={f.id}
                              factura={f}
                              marcada={!desmarcadas.has(f.id)}
                              alAlternar={() => alternar(f.id)}
                              prediosFuera={prediosFuera}
                              alAlternarPredio={alternarPredio}
                              sinCuenta={sinCuenta}
                            />
                          );
                          if (cuentas.length < 2) {
                            return grupo.facturas.map((f) => fila(f, false));
                          }
                          return cuentas.map((c) => {
                            const deLaCuenta = seleccion.filter((f) => c.facturas.includes(f));
                            return (
                              <CuentaDelGrupo
                                key={c.numeroCuenta || 'varias'}
                                numeroCuenta={c.numeroCuenta}
                                direccion={c.direccion}
                                cuantas={c.facturas.length}
                                marcadas={deLaCuenta.length}
                                importe={deLaCuenta.reduce(
                                  (suma, f) => suma + loDe(f, 'capital') + loDe(f, 'interes'),
                                  0,
                                )}
                                alAlternar={() => alternarGrupo(c.facturas)}
                              >
                                {c.facturas.map((f) => fila(f, c.numeroCuenta !== ''))}
                              </CuentaDelGrupo>
                            );
                          });
                        })()}
                      </GrupoDeFacturas>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => setAdelantando(true)}
                  className="flex w-full items-center justify-center gap-2 border-t border-armazon-100 px-5 py-3 text-[11px] font-semibold uppercase text-armazon-500 transition-colors hover:bg-armazon-50 hover:text-marca-700"
                >
                  <CalendarPlus className="size-3.5" aria-hidden />
                  Adelantar meses que aún no se han facturado
                </button>
              </div>

              {art8}

              {puedePerdonar && (
                <CobroSinMora
                  activo={sinMora}
                  alCambiar={setSinMora}
                  interes={interes}
                  motivo={motivoSinMora}
                  alCambiarMotivo={setMotivoSinMora}
                  faltaElMotivo={faltaElMotivo}
                />
              )}
            </div>

            <PanelDeCobro
              seleccionadas={seleccion.length}
              capital={capital}
              interes={interesACobrar}
              metodo={metodo}
              alCambiarMetodo={setMetodo}
              numeroComprobante={comprobanteMostrado}
              alCambiarComprobante={(v) => {
                setComprobanteTocado(true);
                setNumeroComprobante(v);
              }}
              caja={proximo.data?.nombreDeLaCaja ?? null}
              sinCaja={sinCaja}
              referenciaBancaria={referenciaBancaria}
              alCambiarReferencia={setReferenciaBancaria}
              banco={banco}
              alCambiarBanco={setBanco}
              reparto={reparto}
              alCambiarReparto={setReparto}
              avisoDelReparto={faltaDelReparto}
              observaciones={observaciones}
              alCambiarObservaciones={setObservaciones}
              error={error}
              cobrando={cobrar.isPending}
              listo={listo}
              alCobrar={() => cobrar.mutate()}
            />
          </div>
        )
      )}

      {cliente && (
        <PanelDeAnticipacion
          cliente={cliente}
          abierto={adelantando}
          alCerrar={() => setAdelantando(false)}
          alGenerar={() => {
            setDesmarcadas(new Set());
            void clienteDeConsultas.invalidateQueries({ queryKey: ['cobro-deuda'] });
          }}
        />
      )}
    </Modal>
  );
}

/**
 * Hasta qué mes tiene pagado el servicio. Lo pidió EPAGAL: «no debe nada» no contesta lo que
 * pregunta quien llega a la ventanilla. Con un predio es una frase; con varios, una por cuenta,
 * porque cada inquilino puede ir a su ritmo.
 */
function PagadoHastaDe({ cuentas }: { cuentas: PagadoHasta[] }) {
  const [unica] = cuentas;
  if (unica === undefined) {
    return null;
  }

  if (cuentas.length === 1) {
    return (
      <p className="mt-3 text-sm uppercase text-marca-800">
        Servicio pagado hasta <span className="font-bold">{nombreDelMes(unica.periodo)}</span>
      </p>
    );
  }

  return (
    <ul className="mx-auto mt-4 max-w-md space-y-1.5 text-left">
      {cuentas.map((c) => (
        <li
          key={c.numeroCuenta}
          className="flex items-baseline justify-between gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-marca-200"
        >
          <span className="min-w-0">
            <span className="importe block text-[11px] font-semibold text-armazon-800">
              {c.numeroCuenta}
            </span>
            <span className="block truncate text-[10px] uppercase text-armazon-500">
              {c.direccionServicio || 'Sin dirección registrada'}
            </span>
          </span>
          <span className="shrink-0 text-[11px] uppercase text-marca-800">
            pagado hasta <span className="font-bold">{nombreDelMes(c.periodo)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Avisos({ deuda: d }: { deuda: DeudaParaCobrar }) {
  return (
    <>
      {!d.tasaAlDia && (
        <p className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-justify text-xs uppercase leading-relaxed text-amber-800">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">
              La tasa de mora no está actualizada al trimestre de hoy.
            </span>{' '}
            Se está aplicando el {d.tasaMensual} % mensual, que es la última cargada. Cárguela en
            Mantenimientos › Tarifas › Interés por mora antes de seguir cobrando.
          </span>
        </p>
      )}

      {d.moraApagada && (
        <p className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-justify text-xs uppercase leading-relaxed text-blue-800">
          <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">El cobro de mora está apagado para todos.</span> Se
            cobra el capital de las facturas y nada más, hasta que se vuelva a encender en
            Mantenimientos › Tarifas › Interés por mora.
          </span>
        </p>
      )}

      {d.exentoInteresMora && !d.moraApagada && (
        <p className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-justify text-xs uppercase leading-relaxed text-blue-800">
          <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">Este cliente está exento de interés por mora.</span> Se
            le cobra el capital de sus facturas y nada más.
          </span>
        </p>
      )}
    </>
  );
}

/**
 * Cobrar sin la mora. Para las entidades públicas que pagan por transferencia, generan su CUR
 * y el pago se registra días después: la mora de esos días no es suya. El servidor exige el
 * permiso y el motivo; aquí solo se pide y se enseña lo que se deja de cobrar.
 */
function CobroSinMora({
  activo,
  alCambiar,
  interes,
  motivo,
  alCambiarMotivo,
  faltaElMotivo,
}: {
  activo: boolean;
  alCambiar: (activo: boolean) => void;
  interes: number;
  motivo: string;
  alCambiarMotivo: (motivo: string) => void;
  faltaElMotivo: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border p-4 ${
        activo ? 'border-red-200 bg-red-50' : 'border-armazon-200 bg-white'
      }`}
    >
      <label className="flex items-start gap-3 text-xs uppercase text-armazon-700">
        <input
          type="checkbox"
          checked={activo}
          onChange={(e) => alCambiar(e.target.checked)}
          className="mt-0.5 size-4 rounded border-armazon-300"
        />
        <span>
          <span className="flex items-center gap-1.5 font-semibold">
            <PercentCircle className="size-3.5" aria-hidden />
            Cobrar sin interés
          </span>
          <span className="mt-0.5 block text-[11px] text-armazon-500">
            Se cobra el capital y se perdonan ${interes.toFixed(2)} de mora. Queda anotado quién lo
            hizo y por qué.
          </span>
        </span>
      </label>

      {activo && (
        <label className="mt-3 grid gap-1.5">
          <span className="text-[10px] font-semibold uppercase text-red-800">
            Por qué · por ejemplo, el número del CUR de la transferencia
          </span>
          <input
            value={motivo}
            onChange={(e) => alCambiarMotivo(e.target.value)}
            maxLength={500}
            autoFocus
            className="h-10 w-full rounded-lg border border-red-200 bg-white px-3 text-sm outline-none focus:border-red-400"
          />
          {faltaElMotivo && (
            <span className="text-[10px] uppercase text-red-700">
              Escriba el motivo para poder cobrar
            </span>
          )}
        </label>
      )}
    </div>
  );
}

function GrupoDeFacturas({
  nombre,
  cuantas,
  marcadas,
  importe,
  alAlternar,
  children,
}: {
  nombre: string;
  cuantas: number;
  marcadas: number;
  importe: number;
  alAlternar: () => void;
  children: React.ReactNode;
}) {
  const [plegado, setPlegado] = useState(false);
  return (
    <section aria-label={nombre}>
      <div className="sticky top-0 z-10 flex items-center gap-3 border-y border-armazon-200 bg-armazon-100 px-5 py-2">
        <input
          type="checkbox"
          checked={marcadas === cuantas}
          ref={(casilla) => {
            if (casilla) casilla.indeterminate = marcadas > 0 && marcadas < cuantas;
          }}
          onChange={alAlternar}
          aria-label={`Marcar todo ${nombre}`}
          className="size-4 shrink-0 accent-marca-600"
        />
        <button
          type="button"
          onClick={() => setPlegado(!plegado)}
          aria-expanded={!plegado}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-[11px] font-bold uppercase text-armazon-800"
        >
          <ChevronRight
            className={`size-3.5 shrink-0 transition-transform ${plegado ? '' : 'rotate-90'}`}
            aria-hidden
          />
          <span className="truncate">{nombre}</span>
          <span className="shrink-0 font-semibold text-armazon-500">
            · {marcadas === cuantas ? cuantas : `${marcadas} de ${cuantas}`}{' '}
            {cuantas === 1 ? 'factura' : 'facturas'}
          </span>
        </button>
        <span className="importe shrink-0 text-sm font-bold text-armazon-900">
          ${importe.toFixed(2)}
        </span>
      </div>
      {!plegado && <div className="divide-y divide-armazon-100">{children}</div>}
    </section>
  );
}

/** Una cuenta dentro del concepto: su cabecera dice cuál es, y debajo van sus meses. */
function CuentaDelGrupo({
  numeroCuenta,
  direccion,
  cuantas,
  marcadas,
  importe,
  alAlternar,
  children,
}: {
  numeroCuenta: string;
  direccion: string | null;
  cuantas: number;
  marcadas: number;
  importe: number;
  alAlternar: () => void;
  children: React.ReactNode;
}) {
  const nombre = numeroCuenta ? `cuenta ${numeroCuenta}` : 'varias cuentas';
  return (
    <section aria-label={nombre} className="border-t-2 border-red-400 first:border-t-0">
      <div className="flex items-center gap-3 bg-red-50 px-5 py-2">
        <input
          type="checkbox"
          checked={marcadas === cuantas}
          ref={(casilla) => {
            if (casilla) casilla.indeterminate = marcadas > 0 && marcadas < cuantas;
          }}
          onChange={alAlternar}
          aria-label={`Marcar todo ${nombre}`}
          className="size-4 shrink-0 accent-marca-600"
        />
        <span className="min-w-0 flex-1">
          <span className="importe block text-xs font-bold text-red-700">
            {numeroCuenta ? `CTA ${numeroCuenta}` : 'VARIAS CUENTAS'}
            <span className="ml-1.5 font-sans font-semibold text-red-600/80">
              · {marcadas === cuantas ? cuantas : `${marcadas} de ${cuantas}`}{' '}
              {cuantas === 1 ? 'mes' : 'meses'}
            </span>
          </span>
          {direccion && numeroCuenta && (
            <span className="block truncate text-[10px] uppercase text-red-700/70">
              {direccion}
            </span>
          )}
        </span>
        <span className="importe shrink-0 text-xs font-bold text-red-700">
          ${importe.toFixed(2)}
        </span>
      </div>
      <div className="divide-y divide-armazon-100">{children}</div>
    </section>
  );
}

function FilaDeFactura({
  factura: f,
  marcada,
  alAlternar,
  prediosFuera,
  alAlternarPredio,
  sinCuenta = false,
}: {
  factura: FacturaParaCobrar;
  marcada: boolean;
  alAlternar: () => void;
  prediosFuera: Set<number>;
  alAlternarPredio: (detalleId: number) => void;
  /** La cuenta ya la dice la cabecera de su grupo. */
  sinCuenta?: boolean;
}) {
  // Una factura de un solo predio no tiene nada que elegir: desplegarla sería ruido.
  const predios = f.predios ?? [];
  const conPredios = predios.length > 1;
  const dentro = predios.filter((p) => !prediosFuera.has(p.detalleId));
  const [abierta, setAbierta] = useState(false);

  const capital = conPredios ? dentro.reduce((s, p) => s + p.capital, 0) : f.capital;
  const interes = conPredios ? dentro.reduce((s, p) => s + p.interes, 0) : f.interes;
  const aMedias = conPredios && dentro.length > 0 && dentro.length < predios.length;

  return (
    <div className={marcada ? 'bg-marca-50/40' : ''}>
      <label
        className={`flex cursor-pointer flex-wrap items-center gap-3 px-5 py-3 transition-colors ${
          marcada ? '' : 'hover:bg-armazon-50/60'
        }`}
      >
        <input
          type="checkbox"
          checked={marcada}
          onChange={alAlternar}
          className="size-4 shrink-0 accent-marca-600"
        />

        <span className="importe w-20 shrink-0 text-xs font-semibold text-armazon-800">
          {f.periodo}
        </span>

        <span className="min-w-0 flex-1 basis-52">
          {sinCuenta ? null : f.numeroCuenta ? (
            <>
              <span className="importe block truncate text-[11px] text-armazon-700">
                {f.numeroCuenta}
              </span>
              <span className="block truncate text-[10px] uppercase text-armazon-400">
                {f.direccionServicio}
              </span>
            </>
          ) : conPredios ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setAbierta(!abierta);
              }}
              className="flex items-center gap-1 text-[11px] font-semibold uppercase text-marca-700 transition-colors hover:text-marca-800"
            >
              <ChevronRight
                className={`size-3.5 transition-transform ${abierta ? 'rotate-90' : ''}`}
                aria-hidden
              />
              {aMedias
                ? `${dentro.length} de ${predios.length} predios`
                : `${predios.length} predios`}
            </button>
          ) : f.direccionServicio ? (
            <span className="block truncate text-[11px] font-semibold uppercase text-marca-700">
              {f.direccionServicio}
            </span>
          ) : (
            !etiquetaDelConcepto(f.concepto) && (
              <span className="text-[11px] uppercase text-armazon-400">Todos sus predios</span>
            )
          )}
        </span>

        <span className="w-24 shrink-0 text-[10px] uppercase text-armazon-500">
          Venció {formatearCorto(f.vencimiento)}
        </span>

        <span className="w-28 shrink-0 text-right">
          <span className="importe block text-sm font-semibold text-armazon-800">
            ${(capital + interes).toFixed(2)}
          </span>
          {interes > 0 && (
            <span className="block text-[10px] uppercase text-amber-700">
              +${interes.toFixed(2)} · {f.mesesMora} {f.mesesMora === 1 ? 'mes' : 'meses'}
            </span>
          )}
        </span>
      </label>

      {conPredios && abierta && marcada && (
        <div className="border-t border-armazon-100 bg-white/60 px-5 py-2 pl-12">
          <p className="mb-1 text-[10px] uppercase leading-relaxed text-armazon-500">
            Se puede cobrar un predio y dejar los demás debiendo
          </p>
          {predios.map((p) => (
            <label
              key={p.detalleId}
              className="flex cursor-pointer items-center gap-3 py-1.5 text-[11px]"
            >
              <input
                type="checkbox"
                checked={!prediosFuera.has(p.detalleId)}
                onChange={() => alAlternarPredio(p.detalleId)}
                className="size-3.5 shrink-0 accent-marca-600"
              />
              <span className="importe w-28 shrink-0 text-armazon-700">{p.numeroCuenta}</span>
              <span className="min-w-0 flex-1 truncate uppercase text-armazon-400">
                {p.direccion || 'Sin dirección registrada'}
              </span>
              <span className="importe w-20 shrink-0 text-right font-semibold text-armazon-700">
                ${p.total.toFixed(2)}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function PanelDeCobro({
  unidad = ['factura', 'facturas'],
  vacio = 'Ninguna factura marcada',
  seleccionadas,
  capital,
  interes,
  metodo,
  alCambiarMetodo,
  numeroComprobante,
  alCambiarComprobante,
  caja,
  sinCaja,
  referenciaBancaria,
  alCambiarReferencia,
  banco,
  alCambiarBanco,
  reparto = null,
  alCambiarReparto,
  avisoDelReparto = null,
  observaciones,
  alCambiarObservaciones,
  error,
  cobrando,
  listo,
  alCobrar,
}: {
  unidad?: [string, string];
  vacio?: string;
  seleccionadas: number;
  capital: number;
  interes: number;
  metodo: string;
  alCambiarMetodo: (v: string) => void;
  numeroComprobante: string;
  alCambiarComprobante: (v: string) => void;

  caja: string | null;
  sinCaja: boolean;
  referenciaBancaria: string;
  alCambiarReferencia: (v: string) => void;
  banco: string;
  alCambiarBanco: (v: string) => void;
  /** El reparto entre dos medios; sin {@link alCambiarReparto} no se ofrece. */
  reparto?: Reparto | null;
  alCambiarReparto?: (v: Reparto | null) => void;
  avisoDelReparto?: string | null;
  observaciones: string;
  alCambiarObservaciones: (v: string) => void;
  error: string | null;
  cobrando: boolean;
  listo: boolean;
  alCobrar: () => void;
}) {
  const porBanco = metodo !== 'efectivo';
  const conCheque = metodo === 'cheque';
  const [anotando, setAnotando] = useState(false);

  return (
    <div className="h-fit space-y-4 rounded-2xl border border-armazon-200 bg-white p-5 lg:sticky lg:top-4">
      <div className="-m-5 mb-0 rounded-t-2xl bg-armazon-800 p-5 text-white">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-armazon-300">
          A cobrar
        </p>
        <p className="importe mt-0.5 text-4xl font-bold leading-none">
          ${(capital + interes).toFixed(2)}
        </p>
        <p className="mt-2 text-[10px] uppercase text-armazon-300">
          {seleccionadas === 0
            ? vacio
            : `${seleccionadas} ${seleccionadas === 1 ? unidad[0] : unidad[1]} · servicio $${capital.toFixed(2)}`}
          {interes > 0 && <span className="text-amber-300"> · interés ${interes.toFixed(2)}</span>}
        </p>
      </div>

      <Campo etiqueta={reparto ? 'Primer medio de pago' : 'Medio de pago'}>
        <span className="grid grid-cols-2 gap-1.5">
          {MEDIOS.map((m, i) => (
            <button
              key={m.codigo}
              type="button"
              onClick={() => alCambiarMetodo(m.codigo)}
              aria-pressed={metodo === m.codigo}
              className={`h-9 rounded-lg border text-[11px] font-semibold uppercase transition-colors ${
                i === MEDIOS.length - 1 && MEDIOS.length % 2 === 1 ? 'col-span-2 ' : ''
              }${
                metodo === m.codigo
                  ? 'border-marca-600 bg-marca-50 text-marca-700'
                  : 'border-armazon-200 text-armazon-500 hover:border-armazon-300'
              }`}
            >
              {m.nombre}
            </button>
          ))}
        </span>
      </Campo>

      {alCambiarReparto &&
        (reparto ? (
          <ElReparto
            total={capital + interes}
            metodo={metodo}
            reparto={reparto}
            alCambiar={alCambiarReparto}
            aviso={avisoDelReparto}
          />
        ) : (
          <button
            type="button"
            onClick={() =>
              alCambiarReparto({
                importe: '',
                medio: metodo === 'efectivo' ? 'transferencia' : 'efectivo',
                referenciaBancaria: '',
                banco: '',
              })
            }
            className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-armazon-500 transition-colors hover:text-marca-700"
          >
            <Split className="size-3.5" aria-hidden />
            Repartir entre dos medios
          </button>
        ))}

      <Campo
        etiqueta="Comprobante"
        ayuda={
          caja
            ? `Serie de ${caja}. Cámbielo solo si su talonario va por otro número.`
            : 'Cámbielo solo si su talonario va por otro número.'
        }
      >
        <input
          value={numeroComprobante}
          onChange={(e) => alCambiarComprobante(e.target.value)}
          maxLength={60}
          className={`${ENTRADA} importe font-semibold`}
        />
      </Campo>

      {sinCaja && (
        <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            No tiene punto de emisión asignado: el recibo saldrá con la numeración general. Pídale
            al administrador que le asigne una caja.
          </span>
        </p>
      )}

      {porBanco && (
        <Campo
          etiqueta={conCheque ? 'Número del cheque' : 'Referencia bancaria'}
          ayuda={conCheque ? 'El que trae impreso el cheque.' : 'La del banco o de la transacción.'}
        >
          <input
            value={referenciaBancaria}
            onChange={(e) => alCambiarReferencia(e.target.value)}
            maxLength={120}
            className={ENTRADA}
          />
        </Campo>
      )}

      {conCheque && (
        <Campo etiqueta="Banco girador" ayuda="El banco contra el que se gira el cheque.">
          <input
            value={banco}
            onChange={(e) => alCambiarBanco(e.target.value)}
            maxLength={80}
            className={ENTRADA}
          />
        </Campo>
      )}

      {observaciones === '' && !anotando ? (
        <button
          type="button"
          onClick={() => setAnotando(true)}
          className="text-[11px] font-semibold uppercase text-armazon-500 transition-colors hover:text-marca-700"
        >
          + Añadir una observación
        </button>
      ) : (
        <Campo etiqueta="Observaciones">
          <textarea
            value={observaciones}
            onChange={(e) => alCambiarObservaciones(e.target.value)}
            rows={2}
            maxLength={2000}
            autoFocus
            className={`${ENTRADA} h-auto py-2`}
          />
        </Campo>
      )}

      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-900"
        >
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={alCobrar}
        disabled={!listo}
        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-marca-600 text-sm font-bold uppercase text-white transition-colors hover:bg-marca-700 disabled:opacity-40"
      >
        {cobrando ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Banknote className="size-4" aria-hidden />
        )}
        Cobrar ${(capital + interes).toFixed(2)}
      </button>

      <p className="text-center text-[10px] uppercase leading-relaxed text-armazon-400">
        El importe lo confirma el servidor con la tasa del día
      </p>
    </div>
  );
}

/**
 * El reparto entre dos medios, para el caso que pidió EPAGAL: «de los 39,38, veinte
 * transferidos y el resto en efectivo».
 *
 * Solo se escribe un importe —el del primer medio— y el segundo se lleva lo que queda. Es un
 * número menos que teclear y, sobre todo, hace imposible que los dos no sumen el total, que es
 * lo que descuadraría el arqueo del día.
 */
function ElReparto({
  total,
  metodo,
  reparto,
  alCambiar,
  aviso,
}: {
  total: number;
  metodo: string;
  reparto: Reparto;
  alCambiar: (v: Reparto | null) => void;
  aviso: string | null;
}) {
  const resto = restoDelReparto(total, reparto);

  return (
    <div className="space-y-3 rounded-xl border border-marca-200 bg-marca-50/50 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-marca-800">
          <Split className="size-3.5" aria-hidden />
          Repartido entre dos medios
        </p>
        <button
          type="button"
          onClick={() => alCambiar(null)}
          className="text-[10px] font-semibold uppercase text-armazon-500 transition-colors hover:text-red-700"
        >
          Quitar
        </button>
      </div>

      <Campo etiqueta={`Cuánto con ${nombreDelMedio(metodo).toLowerCase()}`}>
        <input
          value={reparto.importe}
          onChange={(e) => alCambiar({ ...reparto, importe: e.target.value })}
          inputMode="decimal"
          placeholder="0.00"
          autoFocus
          className={`${ENTRADA} importe font-semibold`}
        />
      </Campo>

      <Campo etiqueta="El resto, con">
        <select
          value={reparto.medio}
          onChange={(e) => alCambiar({ ...reparto, medio: e.target.value })}
          className={ENTRADA}
        >
          {MEDIOS.filter((m) => m.codigo !== metodo).map((m) => (
            <option key={m.codigo} value={m.codigo}>
              {m.nombre}
            </option>
          ))}
        </select>
      </Campo>

      {PIDE_REFERENCIA(reparto.medio) && (
        <Campo
          etiqueta={reparto.medio === 'cheque' ? 'Número del cheque' : 'Referencia de esa parte'}
        >
          <input
            value={reparto.referenciaBancaria}
            onChange={(e) => alCambiar({ ...reparto, referenciaBancaria: e.target.value })}
            maxLength={120}
            className={ENTRADA}
          />
        </Campo>
      )}

      {PIDE_BANCO(reparto.medio) && (
        <Campo etiqueta="Banco girador">
          <input
            value={reparto.banco}
            onChange={(e) => alCambiar({ ...reparto, banco: e.target.value })}
            maxLength={80}
            className={ENTRADA}
          />
        </Campo>
      )}

      {aviso ? (
        <p className="text-[11px] uppercase leading-relaxed text-amber-800">{aviso}</p>
      ) : (
        <p className="flex items-baseline justify-between gap-2 border-t border-marca-200 pt-2 text-[11px] font-semibold uppercase text-marca-800">
          <span>{nombreDelMedio(reparto.medio)}</span>
          <span className="importe text-sm">${resto.toFixed(2)}</span>
        </p>
      )}

      <p className="text-[10px] uppercase leading-relaxed text-armazon-500">
        El recibo saldrá con los dos medios y su importe
      </p>
    </div>
  );
}

function BuscadorDeCliente({
  busqueda,
  alBuscar,
  clientes,
  buscando,
  elegido,
  alElegir,
  alLimpiar,
}: {
  busqueda: string;
  alBuscar: (v: string) => void;
  clientes: Cliente[];
  buscando: boolean;
  elegido: Cliente | null;
  alElegir: (c: Cliente) => void;
  alLimpiar: () => void;
}) {
  return (
    <div className="mb-5 rounded-2xl border border-armazon-200 bg-white p-5">
      {elegido ? (
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-3">
            <Icono identificacion={elegido.identificacion} grande />
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold uppercase text-armazon-800">
                {elegido.nombre}
              </span>
              <span className="importe block text-xs text-armazon-500">
                {elegido.identificacion}
              </span>
            </span>
          </span>
          <button
            type="button"
            onClick={alLimpiar}
            className="shrink-0 text-[11px] font-semibold uppercase text-marca-700 hover:underline"
          >
            Atender a otra persona
          </button>
        </div>
      ) : null}
      {elegido && <AvisoDeConvenio clienteId={elegido.id} />}
      {!elegido && (
        <label className="grid gap-2">
          <span className="text-sm font-semibold uppercase text-armazon-900">
            Quién viene a pagar
          </span>

          <span className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
              aria-hidden
            />
            <input
              value={busqueda}
              onChange={(e) => alBuscar(e.target.value)}
              placeholder="Nombre, cédula, RUC, cuenta o medidor…"
              autoFocus
              className="h-11 w-full rounded-lg border border-armazon-200 bg-white pl-9 pr-3 text-sm outline-none transition-colors focus:border-marca-400"
            />
          </span>

          {busqueda.trim().length >= 3 && (
            <ul className="max-h-64 overflow-y-auto rounded-lg border border-armazon-200">
              {buscando && (
                <li className="px-3 py-2 text-xs uppercase text-armazon-400">Buscando…</li>
              )}
              {!buscando && clientes.length === 0 && (
                <li className="px-3 py-2 text-xs uppercase text-armazon-400">
                  Ningún cliente coincide
                </li>
              )}
              {clientes.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => alElegir(c)}
                    className="flex w-full items-center gap-2.5 border-b border-armazon-100 px-3 py-2 text-left transition-colors last:border-b-0 hover:bg-armazon-50"
                  >
                    <Icono identificacion={c.identificacion} />
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-medium uppercase text-armazon-800">
                        {c.nombre}
                      </span>
                      <span className="importe block text-[10px] text-armazon-500">
                        {c.identificacion}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>
      )}
    </div>
  );
}

function Atajo({
  alPulsar,
  activo,
  children,
}: {
  alPulsar: () => void;
  activo: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      className={`rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase transition-colors ${
        activo ? 'bg-armazon-800 text-white' : 'text-armazon-500 hover:bg-armazon-100'
      }`}
    >
      {children}
    </button>
  );
}

function Campo({
  etiqueta,
  ayuda,
  children,
}: {
  etiqueta: string;
  ayuda?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[10px] font-semibold uppercase text-armazon-600">{etiqueta}</span>
      {children}
      {ayuda && <span className="text-[10px] uppercase text-armazon-400">{ayuda}</span>}
    </label>
  );
}

function Icono({
  identificacion,
  grande = false,
}: {
  identificacion: string | null;
  grande?: boolean;
}) {
  const esInstitucion = (identificacion ?? '').length === 13;
  const caja = grande ? 'size-10' : 'size-7';
  const trazo = grande ? 'size-5' : 'size-3.5';

  return (
    <span
      className={`grid ${caja} shrink-0 place-items-center rounded-lg ${
        esInstitucion ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-500'
      }`}
    >
      {esInstitucion ? (
        <Building2 className={trazo} aria-hidden />
      ) : (
        <UsuarioIcono className={trazo} aria-hidden />
      )}
    </span>
  );
}

const ENTRADA =
  'h-10 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none transition-colors focus:border-marca-400';

function formatearCorto(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}

function rotuloDeLaCaja(cajero: string | undefined, caja: string | null | undefined): string {
  if (!cajero) {
    return caja ?? '';
  }
  return caja ? `Cajero: ${cajero} · ${caja}` : `Cajero: ${cajero}`;
}
